import {doQueryWithoutCache} from 'graphql-client/ApolloWrappers';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import makeCustomResolutionResultFromTypedValue from 'notebook/integrations/samples/transfer-table-columns/makeCustomResolutionResultFromTypedValue';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {
  GetMoleculeCurrentVersionQuery as GetMoleculeCurrentVersionQueryResponse,
  GetMoleculeCurrentVersionQueryVariables,
} from 'notebook/structured-tables/molecules/__generated/GetMoleculeCurrentVersionQuery';
import {GET_MOLECULE_CURRENT_VERSION_QUERY} from 'notebook/structured-tables/molecules/ChemistryQueries';
import {CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import {
  CustomResolutionQueryType,
  GetMoleculeVersionQuery,
  ServerResolutionResult,
} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {BaseTypedValueType, TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';

/**
 * Self-contained value for the Molecule Version column: a bioentity reference (the row's Compound)
 * pinned to a specific version, plus that version's modified date (for the "Last updated" display).
 * Its own TypedValue variant (BaseTypedValueType.MOLECULE_VERSION) rather than a schema-field type, since
 * rendering never needs to reach into the row's other cells to get the Compound's id.
 */
export interface VersionedBioentityTypedValue {
  bioentitySummary: {id: string};
  versionApiId: string;
  /** ISO 8601 timestamp the pinned version was created/last modified. Versions are immutable once
   * created, so this is a fixed fact of `versionApiId`, not a live value that can drift. */
  modifiedAt: string;
}

export function makeMoleculeVersionTypedValue(value: VersionedBioentityTypedValue): TypedValue {
  return {
    type: BaseTypedValueType.MOLECULE_VERSION,
    bioentitySummary: {id: value.bioentitySummary.id},
    versionApiId: value.versionApiId,
    modifiedAt: value.modifiedAt,
  };
}

/** Parses a Molecule Version cell's typed value back into its structured form, or null if the cell is
 * empty or predates this column's dedicated typed value (e.g. a legacy plain-string version id). */
export function getVersionedBioentityFromMoleculeVersionCell(
  typedValue: TypedValue | null | undefined
): VersionedBioentityTypedValue | null {
  if (typedValue?.type !== BaseTypedValueType.MOLECULE_VERSION) {
    return null;
  }
  const {bioentitySummary, versionApiId, modifiedAt} = typedValue;
  if (typeof versionApiId !== 'string' || versionApiId === '' || typeof bioentitySummary?.id !== 'string') {
    return null;
  }
  return {bioentitySummary: {id: bioentitySummary.id}, versionApiId, modifiedAt};
}

export async function bulkResolveGetMoleculeVersionToServerResolutionResult(
  queries: Array<GetMoleculeVersionQuery>
): Promise<Array<ServerResolutionResult>> {
  const moleculeIds = queries.map((query) => query.moleculeId);

  const queryResult = await doQueryWithoutCache<
    GetMoleculeCurrentVersionQueryResponse,
    GetMoleculeCurrentVersionQueryVariables
  >(
    {
      query: GET_MOLECULE_CURRENT_VERSION_QUERY,
    },
    {variables: {moleculeIds}}
  );

  const versionByMoleculeId = new Map<string, {id: string; modifiedAt: string} | null>();
  for (const molecule of queryResult.molecules) {
    if (isInaccessible(molecule)) {
      versionByMoleculeId.set(molecule.invalidId, null);
    } else {
      versionByMoleculeId.set(molecule.id, {
        id: molecule.currentVersion.id,
        modifiedAt: molecule.currentVersion.modifiedAt,
      });
    }
  }

  return queries.map((query) => {
    const version = versionByMoleculeId.get(query.moleculeId);
    if (version === undefined) {
      // Molecule was not returned by the server (e.g. deleted between query construction and execution)
      return makeCustomResolutionResultFromTypedValue(null, 'Could not retrieve molecule version');
    }
    if (version == null) {
      // Molecule exists but is inaccessible -- leave cell empty, no error
      return makeCustomResolutionResultFromTypedValue(null);
    }
    return makeCustomResolutionResultFromTypedValue(
      makeMoleculeVersionTypedValue({
        bioentitySummary: {id: query.moleculeId},
        versionApiId: version.id,
        modifiedAt: version.modifiedAt,
      })
    );
  });
}

export function getMoleculeVersionColumnCustomEval(
  typedValue: TypedValue,
  columnDefinition: ColumnDefinition,
  cellDependencies: Array<TypedValue | null>,
  customEvalOptions: unknown,
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  // The molecule comes from the first cell dependency (the Compound column)
  const bioentityTypedValue = cellDependencies[0];
  if (bioentityTypedValue == null || bioentityTypedValue.type !== BaseTypedValueType.BIOENTITY) {
    return {val: typedValue, error: null, locked: false};
  }
  const moleculeId = bioentityTypedValue.bioentitySummary.id;
  const {resolvedValue, error} = resolveFromResolutionQuery(
    typedValue,
    {
      customResolutionQueryType: CustomResolutionQueryType.STOICHIOMETRY_TABLE_GET_MOLECULE_VERSION,
      moleculeId,
      type: ResolutionQueryType.CUSTOM,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error, locked: false};
}
